import _ from "lodash";
import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import classNames from "classnames";
import { useKey } from "react-use";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import update from "immutability-helper";
import { Trans } from "react-i18next";
import { AI_TRANSLATION_LANGUAGES } from "@toddle-edu/common-utils";
import { Button, IconButton, Tooltip } from "@toddle-edu/ds-web";
import {
  StarsTwoFilled,
  FourLinesOutlined,
  TwoLinesOutlined,
  LeftArrowCircleOutlined,
} from "@toddle-edu/ds-icons";

import { useDispatch } from "Hooks/useDispatch";
import { isIdEqual } from "Utils/idHelpers";
import AIFeedbackStrip from "AppComponents/AIFeedbackStrip";
import { compose } from "lib/apolloClient";
import {
  getDefaultNameSortPreference,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import {
  useChatOptions,
  withChatOptions,
} from "MessagingV2/providers/ChatOptionsProvider";
import ParentMessage from "MessagingV2/components/ParentMessage";
import ChatbarWrapper from "UIComponents/ChatbarWrapper";
import { useEffectEvent, useI18n, useLatestValue } from "Hooks";
import {
  CHANNEL_TYPES,
  CHAT_TYPE,
  MESSAGING_ACTIONS,
  MESSAGING_ACTIONS_FROM,
  getInitialDraftData,
  getIsMessagingWidget,
  isCreateAndManageOwnChannelEnabled,
  isManageChannelsEnabledForNonCreator,
  AI_MESSAGING_FIELD_AND_ACTION,
  recordChatActions,
  MESSAGING_AI_ACTIONS,
  MESSAGING_AI_PROMPT_ACTIONS,
  getIsAIGenerateEnabled,
  getIsAISummariseEnabled,
  isMessagingRevempEnabled,
  isMessagingV2InputEnabled,
  getIsAIDebugModeEnabled,
  getMessageLabelAndAttachmentsForReplyContainer,
  isMessagingCurriculumWiseSplitEnabled,
  isMessagingAudioRecordingEnabled,
  getChatBarDropDownOptions,
  CHAT_BAR_DROPDOWN_ACTION,
  isSendMessageOnShiftEnterEnabled,
  getFormattedNameForUserType,
  isInitiateMessageEnabled,
} from "MessagingV2/modules/Utils";
import {
  resetMessageQueryParams,
  getMessagingV2ReduxState,
  setShowOverviewModal,
  joinChannel,
  unarchiveOrJoinChannel,
  handleArchiveDepartmentChannel,
  goToMessagingV2FormRoute,
  setFormTemplateModal,
  handleCreateNewChat,
} from "MessagingV2/modules/MessagingModule";
import { getAIResponse, goToRelativeRoute } from "modules/Services";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import SummarisePreviewModal from "MessagingV2/components/SummarisePreviewModal";
import CreatePollModal from "MessagingV2/components/PollsAndForms/CreatePollModal";
import FormTemplateModal from "MessagingV2/components/PollsAndForms/FormTemplateModal";
import { STAFF } from "Constants/stringConstants";

import AIPromptInput, { PROMPT_OPTIONS_GROUP_TYPE } from "../AIPromptInput";
import AIResponseSuggestions from "../AIResponseSuggestions";
import classes from "./Footer.scss";
import ChannelUnArchiveModal from "MessagingV2/components/Channels/ChannelUnArchiveModal";
import { getMemoizedLangFunction, onEnterOrSpaceKeyDown } from "Utils";

const {
  SHORTEN,
  ELABORATE,
  IMPROVE_WRITING,
  SIMPLIFY_WRITING,
  CHANGE_THIS_TEXT,
  SPELLING_AND_GRAMMAR,
} = MESSAGING_AI_ACTIONS;

const { REPLACE, KEEP, REGENERATE, DISCARD } = MESSAGING_AI_PROMPT_ACTIONS;

const replaceBetween = ({ str, start, end, content }) => {
  // the end variable is selectionEnd, meaning the index of the character after the selection
  // so use substring with end to get the string after the selection instead of end + 1
  return `${str.substring(0, start)}${content}${str.substring(end)}`;
};

const StartChatLink = ({ creatorName, onStartChatWithCreator, children }) => {
  return (
    <Tooltip tooltip={creatorName}>
      <span
        role="button"
        tabIndex={0}
        aria-label={creatorName}
        className={classes.link}
        onClick={onStartChatWithCreator}
        onKeyDown={e => onEnterOrSpaceKeyDown(e, onStartChatWithCreator)}
      >
        {children}
      </span>
    </Tooltip>
  );
};

const getFooterNote = ({
  isArchived,
  isChat,
  t,
  viewOnlyRoles,
  userType,
  isUserPartOfChat,
  conversationType,
  unarchiveOrJoinChannel,
  chatId,
  isUserCreator,
  isBlocked,
  setChannelOptionsModalState,
  createdBy,
  isWidget,
  handleCreateNewChat,
}) => {
  const isUIRevempEnabled = isMessagingRevempEnabled();
  const canManageChannel =
    (isUserCreator && isCreateAndManageOwnChannelEnabled()) ||
    (!isUserCreator && isManageChannelsEnabledForNonCreator());

  if (isArchived) {
    const onUnarchiveChannel = () => {
      if (isUIRevempEnabled) {
        setChannelOptionsModalState(MESSAGING_ACTIONS.UNARCHIVE);
      } else {
        unarchiveOrJoinChannel({ chatId, unarchiveChannel: true });
        recordChatActions({
          action: MESSAGING_ACTIONS.UNARCHIVE,
          chatId,
          from: MESSAGING_ACTIONS_FROM.FOOTER,
          chatType: conversationType,
        });
      }
    };

    const isCustomOrPublicChannel = _.includes(
      [CHANNEL_TYPES.CHANNEL_CUSTOM, CHANNEL_TYPES.CHANNEL_PUBLIC],
      conversationType
    );

    const handleUnarchiveKeyDown = canManageChannel
      ? e => onEnterOrSpaceKeyDown(e, onUnarchiveChannel)
      : null;

    if (isChat) {
      return t("common:chat_archived_user_note");
    }

    if (isCustomOrPublicChannel) {
      return (
        <Trans i18nKey={"common:channel_archived_user_note"}>
          <span
            role={canManageChannel ? "button" : null}
            tabIndex={canManageChannel ? 0 : null}
            aria-label={canManageChannel ? t("common:unarchive") : null}
            className={classNames({ [classes.link]: canManageChannel })}
            onClick={canManageChannel ? onUnarchiveChannel : null}
            onKeyDown={handleUnarchiveKeyDown}
          />
        </Trans>
      );
    }

    return t("common:class_channel_archived_user_note"); // class channel
  }

  if (isBlocked) {
    const isClassChannel = _.isEqual(
      conversationType,
      CHANNEL_TYPES.CHANNEL_SYSTEM_MANAGED
    );
    if (isClassChannel) return t("common:user_blocked_msg_in_class_channel");

    const startChatWithCreator = () =>
      handleCreateNewChat({
        id: createdBy.id,
        isWidget,
      });
    const canInitiateChat = isInitiateMessageEnabled();

    const creatorName = getFormattedNameForUserType({
      user: createdBy,
    });

    if (!canInitiateChat) return t("common:user_blocked_msg_without_link");

    return (
      <div className={classes.inlineContainer}>
        <Trans i18nKey={"common:user_blocked_msg_in_channel"}>
          <StartChatLink
            creatorName={creatorName}
            onStartChatWithCreator={startChatWithCreator}
          />
        </Trans>
      </div>
    );
  }

  if (!isChat && !isUserPartOfChat) {
    if (conversationType === CHANNEL_TYPES.CHANNEL_CUSTOM) {
      const onJoinChannel = () => {
        unarchiveOrJoinChannel({ chatId, joinChannel: true });
        recordChatActions({
          action: MESSAGING_ACTIONS.JOIN,
          chatId,
          from: MESSAGING_ACTIONS_FROM.FOOTER,
          chatType: conversationType,
        });
      };
      const handleJoinKeyDown = canManageChannel
        ? e => onEnterOrSpaceKeyDown(e, onJoinChannel)
        : null;

      return (
        <Trans i18nKey={"common:not_part_of_custom_channel_note"}>
          <span
            role={canManageChannel ? "button" : null}
            tabIndex={canManageChannel ? 0 : null}
            aria-label={canManageChannel ? t("common:join_channel") : null}
            className={classNames({ [classes.link]: canManageChannel })}
            onClick={canManageChannel ? onJoinChannel : null}
            onKeyDown={handleJoinKeyDown}
          />
        </Trans>
      );
    } else if (conversationType === CHANNEL_TYPES.CHANNEL_FAMILY) {
      return t("common:not_part_of_family_channel_note");
    } else {
      return t("common:not_part_of_class_channel_note");
    }
  }

  if (_.includes(viewOnlyRoles, userType)) {
    return t("common:chat_view_only_user_note");
  }

  return null;
};

const PublicChannelJoinFooter = ({ chatId }) => {
  const { t } = useI18n(["common"]);

  const dispatch = useDispatch();

  const onJoinChannel = () => {
    dispatch(
      joinChannel({
        chatId,
      })
    );
    recordChatActions({
      action: MESSAGING_ACTIONS.JOIN,
      chatId,
      from: MESSAGING_ACTIONS_FROM.FOOTER,
      chatType: CHANNEL_TYPES.CHANNEL_PUBLIC,
    });
  };

  const onGoToChannelsClick = () => {
    dispatch(
      goToRelativeRoute({
        route: `messaging/public-channels`,
        replacePath: "messaging",
      })
    );
  };

  const onShowOverviewModal = () => {
    dispatch(setShowOverviewModal(true));
  };

  return (
    <div className={classes.publicChannelJoinFooter}>
      <Button
        icon={<LeftArrowCircleOutlined />}
        variant="neutral"
        type="plain"
        size="medium"
        dsVersion="2.0"
        onClick={onGoToChannelsClick}
      >
        {t("common:back_to_channels")}
      </Button>

      <div className={classes.pubicChannelFooterAction}>
        <Button
          variant="neutral"
          type="outlined"
          size="medium"
          dsVersion="2.0"
          onClick={onShowOverviewModal}
        >
          {t("common:view_details")}
        </Button>
        <Button
          variant="progressive"
          type="fill"
          dsVersion="2.0"
          size="medium"
          onClick={onJoinChannel}
        >
          {t("common:join_channel")}
        </Button>
      </div>
    </div>
  );
};

const DepartmentChannelFooter = ({
  chatId,
  isArchived,
  isUserPartOfChat,
  isUserCreator,
  isArchivedByStaff,
  isStaffPresent,
}) => {
  const { t } = useI18n(["common"]);
  const dispatch = useDispatch();

  const canManageChannel =
    isUserCreator || isManageChannelsEnabledForNonCreator();

  const onUnarchiveChannel = () => {
    dispatch(handleArchiveDepartmentChannel({ chatId, isArchived: false }));
    recordChatActions({
      action: MESSAGING_ACTIONS.UNARCHIVE,
      chatId,
      from: MESSAGING_ACTIONS_FROM.FOOTER,
      chatType: CHANNEL_TYPES.CHANNEL_DEPARTMENT,
    });
  };

  const onKeyPressHandle = e => {
    if (canManageChannel) {
      onEnterOrSpaceKeyDown(e, onUnarchiveChannel);
    }
  };

  if (!isStaffPresent) {
    return (
      <div className={classes.departmentChannelFooter}>
        <div className="text-body">
          {t("common:department_channel_no_staff_note")}
        </div>
      </div>
    );
  }

  if (isArchived && isArchivedByStaff) {
    return (
      <div className={classes.departmentChannelFooter}>
        <div className="text-body">
          {t("common:channel_archived_by_admin_note")}
        </div>
      </div>
    );
  }

  if (isArchived) {
    return (
      <div className={classes.departmentChannelFooter}>
        <div className="text-body">
          <Trans i18nKey={"common:channel_archived_user_note"}>
            <span
              role={canManageChannel ? "button" : null}
              aria-label="unarchive-channel"
              tabIndex={0}
              className={classNames(classes.plainTextButton, {
                [classes.link]: canManageChannel,
              })}
              onClick={canManageChannel ? onUnarchiveChannel : null}
              onKeyPress={onKeyPressHandle}
            />
          </Trans>
        </div>
      </div>
    );
  }

  if (!isUserPartOfChat) {
    return (
      <div className={classes.departmentChannelFooter}>
        <div className="text-body">{t("common:not_part_of_channel_note")}</div>
      </div>
    );
  }

  return null;
};

const getFooterComponent = ({
  conversationType,
  isUserPartOfChat,
  isArchived,
  archivedBy,
  isUserCreator,
  chatId,
  users = [],
}) => {
  if (
    _.isEqual(conversationType, CHANNEL_TYPES.CHANNEL_PUBLIC) &&
    !isUserPartOfChat
  ) {
    return <PublicChannelJoinFooter chatId={chatId} />;
  }

  const isStaffPresent = _.some(users, user => _.isEqual(user.type, STAFF));

  if (
    _.isEqual(conversationType, CHANNEL_TYPES.CHANNEL_DEPARTMENT) &&
    (isArchived || !isUserPartOfChat || !isStaffPresent)
  ) {
    const userType = _.get(archivedBy, "type");
    const isArchivedByStaff = _.isEqual(userType, STAFF);
    return (
      <DepartmentChannelFooter
        chatId={chatId}
        isArchived={isArchived}
        isUserPartOfChat={isUserPartOfChat}
        isUserCreator={isUserCreator}
        isArchivedByStaff={isArchivedByStaff}
        isStaffPresent={isStaffPresent}
      />
    );
  }

  return null;
};

const AI_PROMPT_TYPE = {
  GENERATE: "GENERATE",
  ENHANCE: "ENHANCE",
};

const PROMPT_KEYS = {
  SUMMARISE: "summarise",
};

const getPrompts = getMemoizedLangFunction(
  ({
    t,
    isGeneratingSuggestions,
    isGeneratingSummary,
    isAISummariseEnabled,
    isEmptyConversations,
    suggestions,
  }) => {
    const prompts = [
      { action: "regenerate" },
      ...(isGeneratingSuggestions
        ? []
        : _.map(suggestions, suggestion => ({
            prompt: suggestion,
            type: "draft",
          }))),
      ...(isAISummariseEnabled && !isGeneratingSummary
        ? [
            {
              key: PROMPT_KEYS.SUMMARISE,
              action: t("common:summarise_this_conversation"),
              type: "external",
              isDisabled: isEmptyConversations,
            },
          ]
        : []),
      { action: "elaborate" },
      { action: "translate" },
      { action: "shorten" },
      { action: "check_for_spelling_and_grammar" },
      { action: "simplify_writing" },
      { action: "improve_writing" },
    ];

    return prompts;
  }
);

// Deliberately passing label as key, as the label is unique
// and need to be passed to AI backend.
const translateLanguages = _.map(AI_TRANSLATION_LANGUAGES, language => ({
  key: language.label,
  label: language.label,
}));

const SelectionPopover = ({ openAIPrompt, onClickOutside, onActionClick }) => {
  const onClickOutsideLatest = useEffectEvent(onClickOutside);
  const ref = useRef();
  const { t } = useI18n(["progressReport"]);

  const handleAIButtonKeyDown = e => onEnterOrSpaceKeyDown(e, openAIPrompt);

  const options = [
    {
      label: t("progressReport:elaborate"),
      onClick: e => {
        e.stopPropagation();
        onActionClick(ELABORATE);
      },
      icon: <FourLinesOutlined variant="dark-default" />,
    },
    {
      label: t("common:shorten"),
      onClick: e => {
        e.stopPropagation();
        onActionClick(SHORTEN);
      },
      icon: <TwoLinesOutlined variant="dark-default" />,
    },
  ];

  useEffect(() => {
    // add event listener to close on outside click
    const onClick = e => {
      if (!ref.current?.contains(e.target)) onClickOutsideLatest();
    };
    document.addEventListener("click", onClick);

    return () => {
      document.removeEventListener("click", onClick);
    };
  }, [onClickOutsideLatest]);

  return (
    <div ref={ref} className={classes.selectionPopoverContainer}>
      <div
        className={classes.aiButton}
        role="button"
        tabIndex={0}
        onClick={openAIPrompt}
        onKeyDown={handleAIButtonKeyDown}
      >
        <StarsTwoFilled size="xxx-small" variant="dark-default" />
        <span className={classNames("text-body-2", classes.aiButtonText)}>
          {t("common:toddle_ai")}
        </span>
      </div>
      {_.map(options, ({ label, onClick, icon }) => (
        <Tooltip key={label} tooltip={label}>
          <IconButton
            size="x-small"
            variant="plain"
            theme="dark"
            onClick={onClick}
            icon={icon}
          />
        </Tooltip>
      ))}
    </div>
  );
};

const createDropDownOverrideClasses = {
  captureScreenOverlayContainer: classes.captureScreenOverlayContainer,
};

const Footer = props => {
  const {
    chatId,
    screenType = "",
    isArchived,
    archivedBy,
    chatType,
    lastActiveAt,
    users,
    isAIGenerateEnabled,
    isEmptyConversations,
    isAISummariseEnabled,
    isAIV2UIEnabled,
    addAttachmentsInMessageData,
    onChangeMessageData,
    messageData,
    chatPanelRef,
    handleCreateMessage,
    handleUpdateMessageMutation,
    resetMessageQueryParams,
    onParentMessageClick,
    viewOnlyRoles,
    userType,
    currentUserId,
    conversationType,
    unarchiveOrJoinChannel,
    getAIResponse,
    handleGenerateSummary,
    isUserCreator,
    channelLabel,
    isGeneratingSuggestions,
    suggestions,
    curriculumProgramId,
    showAIInput,
    viewOnlyUsers = [],
    createdBy,
    handleCreateNewChat,
  } = props;

  const { t } = useI18n(["common"]);
  const dispatch = useDispatch();
  const isUIRevempEnabled = isMessagingRevempEnabled();
  const isUIRevampV2Enabled = isMessagingV2InputEnabled();

  const { screenMode, activeUserId } = useChatOptions();

  const inputWrapper = useRef(null);
  const aiPromptRef = useRef(null);
  const aiCancelTokenFunc = useRef(_.noop);
  const prevAIQueryInput = useRef(null);

  const [isAIResponseStreaming, setIsAIResponseStreaming] = useState(false);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [aiPromptState, setAIPromptState] = useState({
    openPromptType: null,
    promptOptionsType: null,
    extraData: null, // can be used to save extra data
  });

  // string | null, where string is the selection text
  // null meaning do not show popover
  const [selectionPopoverData, setSelectionPopoverData] = useState(null);
  const [enhanceResponse, setEnhanceResponse] = useState("");
  const [isSummaryDurationModalOpen, setIsSummaryDurationModalOpen] = useState(
    false
  );
  const [channelOptionsModalState, setChannelOptionsModalState] = useState(
    null
  );

  const { openPromptType, promptOptionsType } = aiPromptState;

  const aiDebugObjRef = useRef({});
  const isAIResponseStreamingRef = useLatestValue(isAIResponseStreaming);

  const otherUsers = _.filter(users, user => !isIdEqual(user.id, activeUserId));
  const isWidget = getIsMessagingWidget({ screenMode });

  const isChat = chatType === CHAT_TYPE.CHAT;

  const isBlocked = _.some(viewOnlyUsers, { id: currentUserId });

  const isUserPartOfChat = _.find(users, user =>
    isIdEqual(user.id, currentUserId)
  );

  const collaborators = isChat ? [] : otherUsers;

  const onCreateMessage = messageData => {
    if (isAIResponseStreaming) return;
    handleCreateMessage({
      ...messageData,
      chatId,
      collaborators,
      id: null,
    });
    resetMessageQueryParams();
  };

  const onUpdateMessage = messageData => {
    if (isAIResponseStreaming) return;
    handleUpdateMessageMutation({ chatId, ...messageData, collaborators });
  };

  const cancelEditMessage = () => {
    const initialState = getInitialDraftData();
    onChangeMessageData(chatId, initialState);
    if (_.isFunction(chatPanelRef?.current?.setChatbarContent)) {
      chatPanelRef.current.setChatbarContent(initialState.label);
    }
  };

  const handleCloseChannelDropDownModals = () => {
    setChannelOptionsModalState(null);
  };

  const onUpdateResponse = useCallback(
    data => {
      onChangeMessageData(chatId, data);
    },
    [onChangeMessageData, chatId]
  );

  const onAddAttachment = useCallback(
    ({ attachments }) => addAttachmentsInMessageData(chatId, attachments),
    [chatId, addAttachmentsInMessageData]
  );

  const onParentMessageRemove = useCallback(() => {
    onChangeMessageData(chatId, { parentMessage: null });
  }, [onChangeMessageData, chatId]);

  const footerNote = getFooterNote({
    isArchived,
    t,
    isChat,
    viewOnlyRoles,
    userType,
    isUserPartOfChat,
    conversationType,
    unarchiveOrJoinChannel,
    chatId,
    isUserCreator,
    isBlocked,
    setChannelOptionsModalState,
    createdBy,
    isWidget,
    handleCreateNewChat,
  });

  const footerComponent = getFooterComponent({
    chatId,
    isArchived,
    archivedBy,
    conversationType,
    isUserPartOfChat,
    isUserCreator,
    users,
  });

  const showChatBar = !footerNote && !footerComponent;

  const chatBarWrapperClasses = classNames(classes.chatBarWrapper, {
    [classes.chatBarWrapperWidget]: isWidget,
  });
  const chatBarWrapperV2Classes = classNames(classes.chatBarWrapper, {
    [classes.chatBarWrapperWidgetV2]: isWidget,
    [classes.chatBarWrapperWithRevampV2]: isUIRevampV2Enabled && !isWidget,
    [classes.chatBarWrapperWidgetWithRevampV2]: isUIRevampV2Enabled && isWidget,
  });

  const isEditMode = _.get(messageData, "isEditMode", false);
  const parentMessage = _.get(messageData, "parentMessage", {});
  const getAttachmentPopupContainer = useCallback(() => document.body, []);
  const closeSelectionPopover = useCallback(
    () => setSelectionPopoverData(null),
    []
  );

  const closeAIPrompt = useCallback(() => {
    prevAIQueryInput.current = null;
    setEnhanceResponse("");
    setAIPromptState({ openPromptType: null, promptOptionsType: null });
  }, []);

  useKey(
    "Escape",
    () => {
      if (
        isAIV2UIEnabled ||
        isAIResponseStreaming ||
        !showChatBar ||
        !isAIGenerateEnabled
      ) {
        return;
      }
      // clear selection on escape
      const childTextArea = inputWrapper.current.querySelector("textarea");
      childTextArea.setSelectionRange(0, 0);
      closeSelectionPopover();
    },
    [
      showChatBar,
      isAIResponseStreaming,
      isAIGenerateEnabled,
      closeSelectionPopover,
    ]
  );

  useEffect(() => {
    if (!showChatBar || isAIV2UIEnabled) return;

    const childTextArea = inputWrapper.current?.querySelector("textarea");
    if (!childTextArea || !isAIGenerateEnabled) return;

    const onSelectionChange = e => {
      const { selectionStart, selectionEnd } = e.target;
      const selection = e.target.value.substring(selectionStart, selectionEnd);

      if (!selection || isAIResponseStreamingRef.current) return;
      setSelectionPopoverData({ selection, selectionStart, selectionEnd });
    };

    // To handle different cases of selection removal on key press,
    // track onKeyUp and if the selection is empty, then close the popover
    const onKeyUp = e => {
      const { selectionStart, selectionEnd } = e.target;
      if (selectionStart === selectionEnd) {
        setSelectionPopoverData(null);
      }
    };

    childTextArea.addEventListener("select", onSelectionChange);
    childTextArea.addEventListener("keyup", onKeyUp);

    return () => {
      childTextArea.removeEventListener("select", onSelectionChange);
      childTextArea.removeEventListener("keyup", onKeyUp);
    };
  }, [
    showChatBar,
    isAIV2UIEnabled,
    isAIGenerateEnabled,
    isAIResponseStreamingRef,
  ]);

  // TODO: Confirm the behavior of response generation in empty chat
  const onAIActionButtonClick = useCallback(
    e => {
      // stopPropagation is intentional to prevent firing of the
      // onClickOutside callback inside the AIPromptInput component
      e.stopPropagation();

      // Do not close the prompt if streaming is in progress
      if (isAIResponseStreaming) return;

      // toggle prompt open/close
      setAIPromptState(prev => ({
        openPromptType: prev.openPromptType ? null : AI_PROMPT_TYPE.GENERATE,
        promptOptionsType: null, // reset prompt options on prompt open/close
      }));
    },
    [isAIResponseStreaming]
  );

  const closeSummaryDurationModal = () => setIsSummaryDurationModalOpen(false);

  const onSummaryDurationApply = async ({ after, before }) => {
    closeSummaryDurationModal();
    setIsGeneratingSummary(true);
    await handleGenerateSummary({ chatId, after, before });
    setIsGeneratingSummary(false);
  };

  // TODO: verify stop steaming case
  const onStopStreaming = useCallback(() => {
    aiCancelTokenFunc.current();
    aiCancelTokenFunc.current = _.noop;
  }, []);

  // on unmount or chat change, stop streaming
  useEffect(
    () => () => {
      if (!isAIV2UIEnabled) onStopStreaming();
    },
    [chatId, isAIV2UIEnabled, onStopStreaming]
  );

  const openAIPromptWithEnhanceOptions = e => {
    e.stopPropagation();
    setAIPromptState({
      openPromptType: AI_PROMPT_TYPE.ENHANCE,
      promptOptionsType: PROMPT_OPTIONS_GROUP_TYPE.ENHANCE,
      extraData: { selectionState: selectionPopoverData },
    });
    closeSelectionPopover();
  };

  const onClickOutsideAIPrompt = () => {
    // !Due to the bug in DS, where they are having stale closure
    // !wrapping the callback, we are using the ref to get the latest value
    // TODO: update and use state once DS fixes the bug
    if (isAIResponseStreamingRef.current) return;
    closeAIPrompt();
  };

  const openPromptOptionsAfterCompletion = () => {
    setAIPromptState(prev => ({
      ...prev,
      promptOptionsType:
        prev.openPromptType === AI_PROMPT_TYPE.GENERATE
          ? PROMPT_OPTIONS_GROUP_TYPE.GEN_COMPLETED
          : PROMPT_OPTIONS_GROUP_TYPE.ENHANCE_COMPLETED,
    }));
  };

  const handleMessageAIActions = async ({
    context,
    metadata,
    actionType,
    onDownloadProgress,
  }) => {
    const actionData = AI_MESSAGING_FIELD_AND_ACTION[actionType];
    if (!actionData) return;

    const input = {
      ...actionData,
      context,
      metadata,
      entityId: chatId,
      curriculumProgramId,
    };
    setIsAIResponseStreaming(true);

    // this will help in calling the regenerate action
    prevAIQueryInput.current = input;
    await getAIResponse({
      variables: { input },
      getCancelToken: token => {
        aiCancelTokenFunc.current = token;
      },
      onDownloadProgress: parsedResponse => {
        const {
          usageMetricsId,
          intentUsageMetricsId,
          model,
          temperature,
        } = parsedResponse;
        aiDebugObjRef.current = {
          usageMetricsId,
          intentUsageMetricsId,
          model,
          temperature,
        };
        onDownloadProgress(parsedResponse);
      },
    });

    setIsAIResponseStreaming(false);
    openPromptOptionsAfterCompletion();
    aiPromptRef.current?.focus?.();
  };

  const handleEnhanceMessageActions = async ({
    actionType,
    context,
    metadata,
    // in case user is enhancing again, the previous enhanced result. We need
    // to use the previous enhanced result(enhanceResponse) for querying the AI
    selectedText = enhanceResponse ||
      _.get(aiPromptState, "extraData.selectionState.selection", ""),
  }) => {
    // TODO: can show some error toast for no text selected
    if (!_.trim(selectedText)) return;

    await handleMessageAIActions({
      context: { ...(context ?? {}), selectedText },
      metadata,
      actionType,
      onDownloadProgress: parsedResponse => {
        const { content } = parsedResponse;
        setEnhanceResponse(content);
      },
    });
  };

  const onAIPromptSubmit = async userPrompt => {
    if (!_.trim(userPrompt)) return;

    if (openPromptType === AI_PROMPT_TYPE.ENHANCE) {
      setAIPromptState(prev => ({ ...prev, promptOptionsType: null }));
      await handleEnhanceMessageActions({
        actionType: CHANGE_THIS_TEXT,
        context: { userPrompt },
        metadata: {
          chatId: [chatId],
          messageToReplyId: _.get(parentMessage, "id", undefined),
        },
      });
      return;
    }

    setIsAIResponseStreaming(true);
    const input = {
      ...AI_MESSAGING_FIELD_AND_ACTION.GENERATE_RESPONSE,
      metadata: {
        chatId: [chatId],
        messageToReplyId: _.get(parentMessage, "id", undefined),
      },
      context: { userPrompt },
      curriculumProgramId,
      entityId: chatId,
    };

    prevAIQueryInput.current = input;
    await getAIResponse({
      variables: { input },
      getCancelToken: token => {
        aiCancelTokenFunc.current = token;
      },
      onDownloadProgress: parsedResponse => {
        const {
          content,
          usageMetricsId,
          intentUsageMetricsId,
          model,
          temperature,
        } = parsedResponse;
        aiDebugObjRef.current = {
          usageMetricsId,
          intentUsageMetricsId,
          model,
          temperature,
        };
        onUpdateResponse({ label: content });
      },
    });

    // TODO: check issue on failure case
    setIsAIResponseStreaming(false);
    openPromptOptionsAfterCompletion();

    // ?NOTE: If we highlight the text, then the promptInput can't be focused
    // !and moreover text selection has another action attached to it
    aiPromptRef.current?.focus?.();
  };

  const onSuggestionGenerate = suggestion => {
    if (isAIV2UIEnabled) {
      chatPanelRef.current?.runPrompt?.(suggestion);
      return;
    }

    setAIPromptState({
      openPromptType: AI_PROMPT_TYPE.GENERATE,
      promptOptionsType: null,
    });
    onAIPromptSubmit(suggestion);
  };

  const regenerateResponse = async () => {
    // Although should not happen, but just in case
    if (!prevAIQueryInput.current) return;

    setAIPromptState(prev => ({ ...prev, promptOptionsType: null }));
    setIsAIResponseStreaming(true);
    await getAIResponse({
      variables: { input: prevAIQueryInput.current },
      getCancelToken: token => {
        aiCancelTokenFunc.current = token;
      },
      onDownloadProgress: parsedResponse => {
        const {
          content,
          usageMetricsId,
          intentUsageMetricsId,
          model,
          temperature,
        } = parsedResponse;
        aiDebugObjRef.current = {
          usageMetricsId,
          intentUsageMetricsId,
          model,
          temperature,
        };
        if (openPromptType === AI_PROMPT_TYPE.GENERATE) {
          onUpdateResponse({ label: content });
        } else {
          setEnhanceResponse(content);
        }
      },
    });
    setIsAIResponseStreaming(false);
    openPromptOptionsAfterCompletion();
    aiPromptRef.current?.focus?.();
  };

  const onAIPromptOptionClick = async option => {
    const { key } = option;
    switch (key) {
      case KEEP:
        closeAIPrompt();
        break;
      case REPLACE:
        if (openPromptType === AI_PROMPT_TYPE.ENHANCE) {
          // REPLACE option is only available once the response is enhanced
          const { selectionStart, selectionEnd } = _.get(
            aiPromptState,
            "extraData.selectionState",
            {}
          );
          const updatedLabel =
            _.isUndefined(selectionStart) || _.isUndefined(selectionEnd)
              ? enhanceResponse
              : replaceBetween({
                  str: messageData.label,
                  start: selectionStart,
                  end: selectionEnd,
                  content: enhanceResponse,
                });
          onUpdateResponse({ label: updatedLabel });
        }
        closeAIPrompt();
        break;
      case REGENERATE: {
        await regenerateResponse();
        break;
      }
      case DISCARD:
        if (openPromptType === AI_PROMPT_TYPE.GENERATE) {
          onChangeMessageData(chatId, getInitialDraftData());
        }
        // closing prompt will reset the enhance response
        closeAIPrompt();
        break;
      case ELABORATE:
      case SHORTEN:
      case SIMPLIFY_WRITING:
        // close options dropdown
        setAIPromptState(prev => ({ ...prev, promptOptionsType: null }));
        if (openPromptType === AI_PROMPT_TYPE.GENERATE) {
          await handleMessageAIActions({
            context: { selectedText: messageData.label },
            actionType: key,
            onDownloadProgress: parsedResponse => {
              const { content } = parsedResponse;
              onUpdateResponse({ label: content });
            },
          });
        } else {
          handleEnhanceMessageActions({ actionType: key });
        }
        break;
      case IMPROVE_WRITING:
      case SPELLING_AND_GRAMMAR: {
        if (openPromptType !== AI_PROMPT_TYPE.ENHANCE) return;
        // close options dropdown
        setAIPromptState(prev => ({ ...prev, promptOptionsType: null }));
        handleEnhanceMessageActions({ actionType: key });
        break;
      }
      default:
        break;
    }
  };

  const handleToolbarAction = actionType => {
    setAIPromptState({
      openPromptType: AI_PROMPT_TYPE.ENHANCE,
      promptOptionsType: null,
      extraData: { selectionState: selectionPopoverData },
    });
    // Explicity passing the selected text to enhance, as above react state update
    // is queued(kinda async) and the we need to provide the selected text to enhance
    handleEnhanceMessageActions({
      actionType,
      selectedText: selectionPopoverData?.selection,
    });
    closeSelectionPopover();
  };

  const replyWithAIText = t("common:reply_with_ai");
  const summariseText = t("common:summarise");
  const renderAIActionItems = useCallback(() => {
    if (!showAIInput) return null;
    if (isAIV2UIEnabled) return null;

    return (
      <>
        {isAIGenerateEnabled ? (
          <Tooltip tooltip={replyWithAIText}>
            <IconButton
              icon={<StarsTwoFilled />}
              variant="plain"
              size="small"
              isActivated={openPromptType === AI_PROMPT_TYPE.GENERATE}
              onClick={onAIActionButtonClick}
            />
          </Tooltip>
        ) : null}
        {isAISummariseEnabled ? (
          <Tooltip tooltip={summariseText}>
            <IconButton
              icon={<TwoLinesOutlined />}
              variant="plain"
              size="small"
              disabled={isGeneratingSummary}
              onClick={() => setIsSummaryDurationModalOpen(true)}
            />
          </Tooltip>
        ) : null}
      </>
    );
  }, [
    openPromptType,
    isGeneratingSummary,
    onAIActionButtonClick,
    isAIGenerateEnabled,
    isAISummariseEnabled,
    replyWithAIText,
    summariseText,
    isAIV2UIEnabled,
    showAIInput,
  ]);

  const getAIResponseWrapper = useCallback(
    params => {
      const { variables, ...restParams } = params;
      const newVariables = update(variables, {
        input: {
          $merge: {
            curriculumProgramId,
            entityId: chatId,
            module: "MESSAGING_AI",
          },
        },
      });

      return getAIResponse({
        ...restParams,
        variables: newVariables,
      });
    },
    [getAIResponse, chatId, curriculumProgramId]
  );

  const getChatBarV2Props = () => {
    if (!isAIV2UIEnabled) return {};

    return {
      getAIResponseWrapper,
      prompts: getPrompts({
        isGeneratingSuggestions,
        isGeneratingSummary,
        isAISummariseEnabled,
        isEmptyConversations,
        suggestions,
      }),
      feedbackStrip: getIsAIDebugModeEnabled() ? (
        <AIFeedbackStrip containerStyle={{ borderRadius: "0px 0px 8px 8px" }} />
      ) : null,
      onExternalOptionClick: () => {
        setIsSummaryDurationModalOpen(true);
      },
      plugins: { ai: { isEnabled: isAIV2UIEnabled } },
      translateLanguages,
      metaData: {
        chatId: [chatId],
        messageToReplyId: _.get(parentMessage, "id", undefined),
      },
    };
  };

  const chatBarDropdownOption = useMemo(
    () => getChatBarDropDownOptions({ isChat }),
    [isChat]
  );

  const onDropDownOptionClick = useCallback(
    ({ key }) => {
      switch (key) {
        case CHAT_BAR_DROPDOWN_ACTION.CREATE_POLL:
          setChannelOptionsModalState(MESSAGING_ACTIONS.CREATE_POLL);
          break;
        case CHAT_BAR_DROPDOWN_ACTION.CREATE_NEW_FORM:
          dispatch(
            goToMessagingV2FormRoute({
              chatId,
            })
          );

          break;
        case CHAT_BAR_DROPDOWN_ACTION.SELECT_FORM_TEMPLATE:
          dispatch(setFormTemplateModal(true));
          break;
      }
    },
    [chatId, dispatch]
  );

  const sendMessageOnShiftEnter = isSendMessageOnShiftEnterEnabled();
  return (
    <>
      <div className={classes.footerContainer}>
        {showChatBar ? (
          <>
            {isAIGenerateEnabled && !isEmptyConversations ? (
              <AIResponseSuggestions
                chatId={chatId}
                chatType={chatType}
                lastActiveAt={lastActiveAt}
                isAIResponseStreaming={isAIResponseStreaming}
                onSuggestionGenerate={onSuggestionGenerate}
              />
            ) : null}
            <div
              className={
                isUIRevempEnabled
                  ? chatBarWrapperV2Classes
                  : chatBarWrapperClasses
              }
              ref={inputWrapper}
            >
              {!isAIV2UIEnabled && isAIGenerateEnabled && openPromptType ? (
                <AIPromptInput
                  ref={aiPromptRef}
                  textContent={enhanceResponse}
                  isStreaming={isAIResponseStreaming}
                  onSubmit={onAIPromptSubmit}
                  onClickOutside={onClickOutsideAIPrompt}
                  onStop={onStopStreaming}
                  onOptionClick={onAIPromptOptionClick}
                  aiDebugObjRef={aiDebugObjRef}
                  placeholder={
                    openPromptType === AI_PROMPT_TYPE.GENERATE
                      ? t("common:how_can_i_help_you")
                      : t("common:how_would_you_like_to_change_this")
                  }
                  promptOptionsType={promptOptionsType}
                />
              ) : !isAIV2UIEnabled && selectionPopoverData ? (
                <SelectionPopover
                  onActionClick={handleToolbarAction}
                  openAIPrompt={openAIPromptWithEnhanceOptions}
                  onClickOutside={closeSelectionPopover}
                />
              ) : null}
              <ChatbarWrapper
                hideGlobalTrayOnCapture={false}
                createDropDownOverrideClasses={createDropDownOverrideClasses}
                isStickyContainer={false}
                isAudioRecordingEnabled={isMessagingAudioRecordingEnabled()}
                selectDropDownOptions={chatBarDropdownOption}
                onDropDownOptionClick={onDropDownOptionClick}
                messageData={messageData}
                enableChatPanel={true}
                chatPanelRef={chatPanelRef}
                {...getChatBarV2Props()}
                getAttachmentPopupContainer={getAttachmentPopupContainer}
                sendMessage={isEditMode ? onUpdateMessage : onCreateMessage}
                isTaggingEnabled={!isChat}
                collaborators={collaborators}
                parentType="CHAT"
                inputTextPlaceHolder={t("common:write_a_message")}
                onResponseChange={onUpdateResponse}
                onAddAttachment={onAddAttachment}
                allowInitialLabelValue
                mode={isEditMode ? "edit" : "view"}
                cancelEditMessage={cancelEditMessage}
                renderAIActionItems={renderAIActionItems}
                replyContainer={
                  !isEditMode && !_.isEmpty(parentMessage) ? (
                    <ParentMessage
                      isChildMessageSentByUser // always true, as logged in user is replying
                      isPlainContainer
                      isClosable
                      onCloseClick={onParentMessageRemove}
                      parentMessage={getMessageLabelAndAttachmentsForReplyContainer(
                        { message: parentMessage, isFromChatBar: true }
                      )}
                      goToParentMessage={onParentMessageClick}
                      isFromChatBar={true}
                    />
                  ) : null
                }
                sendOnEnter={!sendMessageOnShiftEnter}
                showNewDesignForCaptureOptions={true}
              />
            </div>
          </>
        ) : null}

        {footerComponent ? (
          footerComponent
        ) : footerNote ? (
          <div className={classNames(classes.footerNotes, "text-subtitle-3")}>
            {footerNote}
          </div>
        ) : null}
      </div>
      {isAISummariseEnabled && isSummaryDurationModalOpen ? (
        <SummarisePreviewModal
          onCloseModal={closeSummaryDurationModal}
          onApplyDuration={onSummaryDurationApply}
        />
      ) : null}
      {_.isEqual(channelOptionsModalState, MESSAGING_ACTIONS.UNARCHIVE) ? (
        <ChannelUnArchiveModal
          chatId={chatId}
          onCloseModal={handleCloseChannelDropDownModals}
          channelLabel={channelLabel}
        />
      ) : null}
      {_.isEqual(channelOptionsModalState, MESSAGING_ACTIONS.CREATE_POLL) ? (
        <CreatePollModal
          chatId={chatId}
          onCloseModal={handleCloseChannelDropDownModals}
        />
      ) : null}
      {/* form template state need to manage from redux to make it keep open on coming backing  */}
      <FormTemplateModal chatId={chatId} />
    </>
  );
};

const mapActionCreators = {
  resetMessageQueryParams,
  goToRelativeRoute,
  unarchiveOrJoinChannel,
  getAIResponse,
  handleCreateNewChat,
};

// User empty array as default value for users till we get the data from the server
// The sole intent behind the existence of this "stableEmptyArr" is to prevent redundant
// re-renders that each time a new default array is used within mapStateToProps.
const stableEmptyArr = [];
const mapStateToProps = (state, ownProps) => {
  const { users = stableEmptyArr, chatId } = ownProps;
  const defaultNameSortPreference = getDefaultNameSortPreference(state);
  const currentUserId = _.get(ownProps, "activeUserId");
  const isPersonalChatPaid = _.get(ownProps, "isPersonalChatPaid");

  const userInfo = getUserInfo();

  const messagingReduxState = getMessagingV2ReduxState(state);
  const { isLoading: isGeneratingSuggestions, suggestions } = _.get(
    messagingReduxState,
    ["aiSuggestions", chatId],
    {
      isLoading: false,
      suggestions: stableEmptyArr,
    }
  );
  const showAIInput =
    !isMessagingCurriculumWiseSplitEnabled() || isPersonalChatPaid;
  const isAIV2UIEnabled = showAIInput && isMessagingV2InputEnabled();

  return {
    users,
    isAIV2UIEnabled,
    showAIInput,
    isAIGenerateEnabled: getIsAIGenerateEnabled(state),
    isAISummariseEnabled: getIsAISummariseEnabled(state),
    currentUserId,
    defaultNameSortPreference,
    userType: userInfo.user_type,
    isGeneratingSuggestions: isAIV2UIEnabled ? isGeneratingSuggestions : false,
    suggestions: isAIV2UIEnabled ? suggestions : stableEmptyArr,
    curriculumProgramId: getSelectedCurriculumId(state),
  };
};

export default compose(
  withChatOptions(),
  connect(mapStateToProps, mapActionCreators)
)(Footer);

Footer.propTypes = {
  chatId: PropTypes.string.isRequired,
  chatType: PropTypes.string.isRequired,
  isArchived: PropTypes.bool.isRequired,
  onParentMessageClick: PropTypes.func.isRequired,
  users: PropTypes.array,
  screenType: PropTypes.string,
  handleCreateMessage: PropTypes.func.isRequired,
  handleUpdateMessageMutation: PropTypes.func.isRequired,
  messageData: PropTypes.object,
  onChangeMessageData: PropTypes.func.isRequired,
  addAttachmentsInMessageData: PropTypes.func.isRequired,
};
